import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { OfferStatus } from '@recruitee/http-types';

import { OfferScope } from '../types';
import { OffersBriefActions } from './offers-brief.actions';
import { OfferItemsActions } from './offers.actions';
import { TalentPoolsBriefActions } from './talent-pools/talent-pools-brief.store';
import { TalentPoolsFullActions } from './talent-pools/talent-pools-full.store';
import { TalentPoolsActions } from './talent-pools/talent-pools.actions';
import {
  TalentPoolItemsActions,
  TalentPoolItemsSelectors,
} from './talent-pools/talent-pools.store';

@Injectable({
  providedIn: 'root',
})
export class TalentPoolsGlobalEffects {
  public fetchActiveCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OfferItemsActions.fetchActiveCollections),
      switchMap(() => [TalentPoolItemsActions.fetchActiveCollection()]),
    ),
  );
  public fetchArchivedCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OfferItemsActions.fetchArchivedCollections),
      switchMap(() => [TalentPoolItemsActions.fetchArchivedCollection()]),
    ),
  );
  public fetchActiveBriefCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OffersBriefActions.fetchActiveCollections),
      switchMap(() => [TalentPoolsBriefActions.fetchActiveCollection()]),
    ),
  );
  public fetchArchivedBriefCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OffersBriefActions.fetchArchivedCollections),
      switchMap(() => [TalentPoolsBriefActions.fetchArchivedCollection()]),
    ),
  );

  public updateFromFull$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.fetchOneSuccess),
      switchMap(({ payload }) => {
        const scope =
          payload.offer.status === OfferStatus.Archived ? OfferScope.Archived : OfferScope.Active;
        return [
          TalentPoolItemsActions.upsertOne({
            payload: {
              data: payload.offer,
              scope,
            },
          }),
          TalentPoolsBriefActions.upsertOne({
            payload: {
              data: payload.offer,
              scope,
            },
          }),
        ];
      }),
    ),
  );
  public updateFromItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolItemsActions.fetchCollectionSuccess),
      switchMap(({ payload }) => [
        TalentPoolsBriefActions.upsertMany({
          payload: { data: payload.data, scope: <OfferScope>payload.scope },
        }),
      ]),
    ),
  );

  public create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsActions.create),
      switchMap(({ payload }) => of(TalentPoolsFullActions.create({ payload }))),
    ),
  );
  public createSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.createSuccess),
      switchMap(({ payload }) => [TalentPoolsActions.createSuccess({ payload })]),
    ),
  );
  public createFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.createFailed),
      switchMap(({ payload }) => [TalentPoolsActions.createFailed({ payload })]),
    ),
  );

  public update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsActions.update),
      switchMap(({ payload }) => of(TalentPoolsFullActions.update({ payload }))),
    ),
  );
  public updateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.updateSuccess),
      switchMap(({ payload }) => [
        TalentPoolsActions.updateSuccess({ payload: { data: payload.data } }),
        TalentPoolItemsActions.upsertOne({
          payload: { data: payload.data, scope: OfferScope.Active },
        }),
        TalentPoolsBriefActions.upsertOne({
          payload: { data: payload.data, scope: OfferScope.Active },
        }),
      ]),
    ),
  );
  public updateFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.updateFailed),
      switchMap(({ payload }) => of(TalentPoolsActions.updateFailed({ payload }))),
    ),
  );

  public remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsActions.removeOne),
      switchMap(({ payload }) => [
        TalentPoolItemsActions.removeOnePessimistic({ payload: { data: payload.id } }),
      ]),
    ),
  );
  public removeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolItemsActions.removeOnePessimisticSuccess),
      switchMap(({ payload }) => [
        TalentPoolsActions.removeOneSuccess({ payload: { id: payload.data.id } }),
        TalentPoolsFullActions.removeOne({ payload: { data: payload.data.id } }),
        TalentPoolsBriefActions.removeOne({ payload: { data: payload.data.id } }),
      ]),
    ),
  );
  public removeFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolItemsActions.removeOnePessimisticFailed),
      switchMap(({ payload }) => [
        TalentPoolsActions.removeOneFailed({
          payload: { id: <number>payload.data, error: payload.error },
        }),
      ]),
    ),
  );

  public duplicate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsActions.duplicate),
      switchMap(({ payload }) => of(TalentPoolsFullActions.duplicate({ payload }))),
    ),
  );
  public duplicateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.duplicateSuccess),
      switchMap(({ payload }) => [
        TalentPoolsActions.duplicateSuccess({ payload }),
        TalentPoolItemsActions.fetchActiveCollection(),
      ]),
    ),
  );
  public duplicateFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.duplicateFailed),
      switchMap(({ payload }) => [TalentPoolsActions.duplicateFailed({ payload })]),
    ),
  );

  public follow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsActions.follow),
      switchMap(({ payload }) => [TalentPoolsFullActions.follow({ payload })]),
    ),
  );
  public followSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.followSuccess),
      switchMap(({ payload }) => {
        const scope =
          payload.talentPool.status === OfferStatus.Archived
            ? OfferScope.Archived
            : OfferScope.Active;
        return [
          TalentPoolsActions.followSuccess(),
          TalentPoolItemsActions.upsertOne({
            payload: { data: payload.talentPool, scope },
          }),
          TalentPoolsBriefActions.upsertOne({
            payload: { data: payload.talentPool, scope },
          }),
        ];
      }),
    ),
  );
  public followFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.followFailed),
      switchMap(({ payload }) => [TalentPoolsActions.followFailed({ payload })]),
    ),
  );

  public unfollow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsActions.unfollow),
      switchMap(({ payload }) => [TalentPoolsFullActions.unfollow({ payload })]),
    ),
  );
  public unfollowSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.unfollowSuccess),
      switchMap(({ payload }) => {
        const scope =
          payload.talentPool.status === OfferStatus.Archived
            ? OfferScope.Archived
            : OfferScope.Active;
        return [
          TalentPoolsActions.unfollowSuccess(),
          TalentPoolItemsActions.upsertOne({
            payload: { data: payload.talentPool, scope },
          }),
          TalentPoolsBriefActions.upsertOne({
            payload: { data: payload.talentPool, scope },
          }),
        ];
      }),
    ),
  );
  public unfollowFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.unfollowFailed),
      switchMap(({ payload }) => [TalentPoolsActions.unfollowFailed({ payload })]),
    ),
  );

  public changeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsActions.changeStatus),
      concatLatestFrom(({ payload }) =>
        this.store.select(TalentPoolItemsSelectors.byId(payload.id)),
      ),
      switchMap(([{ payload }, offer]) =>
        of(
          TalentPoolsFullActions.changeStatus({
            payload: { ...payload, currentStatus: offer!.status },
          }),
        ),
      ),
    ),
  );
  public changeStatusSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.changeStatusSuccess),
      switchMap(({ payload }) => [
        ...(payload.isChangingScope
          ? [
              TalentPoolItemsActions.removeOne({ payload: { data: payload.offer.id } }),
              TalentPoolsBriefActions.removeOne({ payload: { data: payload.offer.id } }),
            ]
          : []),
        TalentPoolItemsActions.upsertOne({
          payload: { data: payload.offer, scope: payload.targetScope },
        }),
        TalentPoolsBriefActions.upsertOne({
          payload: { data: payload.offer, scope: payload.targetScope },
        }),
        TalentPoolsActions.changeStatusSuccess({ payload: { offer: payload.offer } }),
      ]),
    ),
  );
  public changeStatusFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsFullActions.changeStatusFailed),
      switchMap(({ payload }) => [TalentPoolsActions.changeStatusFailed({ payload })]),
    ),
  );

  public updatePositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolsActions.updatePositions),
      switchMap(({ payload }) => [TalentPoolItemsActions.updatePositions({ payload })]),
    ),
  );
  public updatePositionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolItemsActions.updatePositionSuccess),
      switchMap(() => [TalentPoolsActions.updatePositionsSuccess()]),
    ),
  );
  public updatePositionsFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TalentPoolItemsActions.updatePositionsFailed),
      switchMap(({ payload }) => [TalentPoolsActions.updatePositionsFailed({ payload })]),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
  ) {}
}
